/**
 * OccupationalRisk Edit Page
 * Page for editing existing occupational risk
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PageShell } from '../../components';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { OccupationalRisk, OccupationalRiskFormData } from '../../types/occupationalRisk';
import { OccupationalRiskForm } from './components/OccupationalRiskForm';

export const OccupationalRiskEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [risk, setRisk] = useState<OccupationalRisk | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRisk = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await occupationalRiskService.getOccupationalRiskById(id);
        setRisk(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar risco ocupacional');
      } finally {
        setLoading(false);
      }
    };

    loadRisk();
  }, [id]);

  const handleSubmit = async (data: OccupationalRiskFormData) => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await occupationalRiskService.updateOccupationalRisk(id, data);
      navigate('/occupational-risks');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar risco ocupacional');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/occupational-risks');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/home' },
    { label: 'Riscos Ocupacionais', path: '/occupational-risks' },
    { label: 'Editar Risco' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Risco Ocupacional">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!risk) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Risco Ocupacional">
        <Alert severity="error">Risco ocupacional não encontrado</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Editar Risco Ocupacional"
      subtitle={risk.riskFactorName}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <OccupationalRiskForm
            initialData={risk}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            riskId={id}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default OccupationalRiskEditPage;
